import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Phone, MapPin, Send, User, MessageSquare, CheckCircle } from 'lucide-react'
import { Button, ErrorBanner } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
// Ham axios instance'i yerine `api` sarmalayicisi: hatalar ApiError'a normalize
// edilir (backend mesaji + tur bilgisi korunur), yanit gövdesi dogrudan doner.
import { api } from '@/services/api'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'

const ContactScreen: React.FC = () => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    subject: '',
    message: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (isLoading) return

    setIsLoading(true)
    setErrorMessage('')
    
    try {
      const response = await api.post<unknown>('/contact', formData)
      
      if (response.success) {
        setIsSuccess(true)
        setFormData({ name: '', phone: '', subject: '', message: '' })
        
        // Reset success message after 5 seconds
        setTimeout(() => setIsSuccess(false), 5000)
      } else {
        // success:false de bir hatadir: backend mesaji korunur.
        setErrorMessage(responseErrorMessage(response, t, t.contact.error))
      }
    } catch (error) {
      // Eski kod `error.response?.data?.message` okuyordu; ApiError'da boyle bir
      // alan yok, deger HER ZAMAN undefined olup jenerik metne dusuyordu.
      logError('ContactScreen.handleSubmit', error)
      setErrorMessage(toUserMessage(error, t, t.contact.error))
    } finally {
      setIsLoading(false)
    }
  }

  const contactInfo = [
    {
      icon: Phone,
      title: t.contact.phone,
      content: '0 534 495 25 90',
      href: 'tel:+905344952590'
    },
    {
      icon: MapPin,
      title: t.contact.address,
      content: t.footer.fullAddress,
      href: '#'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-secondary-50 py-6 md:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-12"
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-4 md:mb-6">
            {t.contact.title}
          </h1>
          <p className="text-base md:text-xl text-secondary-600 max-w-3xl mx-auto">
            {t.contact.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4 md:space-y-6"
          >


            <div className="space-y-3 md:space-y-4">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 md:w-6 md:h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm md:text-base text-secondary-900 mb-1">{item.title}</h3>
                    <a
                      href={item.href}
                      className="text-xs md:text-sm text-secondary-600 hover:text-primary-600 transition-colors duration-200"
                    >
                      {item.content}
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Working Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-primary-50 rounded-xl p-4 md:p-6 border border-primary-100"
            >
              <h3 className="font-semibold text-sm md:text-base text-secondary-900 mb-3 md:mb-4">{t.contact.workingHours}</h3>
              <div className="space-y-2 text-xs md:text-sm text-secondary-700">
                <div className="flex justify-between">
                  <span>{t.contact.monday} - {t.contact.friday}</span>
                  <span>09:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.contact.saturday}</span>
                  <span>09:00 - 14:00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.contact.sunday}</span>
                  <span>{t.contact.closed}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-lg p-5 md:p-8 border border-secondary-100"
            style={{ position: 'relative', zIndex: 10 }}
          >
            <h2 className="text-xl md:text-2xl font-bold text-secondary-900 mb-4 md:mb-6">
              {t.contact.sendMessage}
            </h2>

            {/* Success Message */}
            {isSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
              >
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-800">{t.contact.success}</p>
              </motion.div>
            )}

            {/* Error Message */}
            <ErrorBanner
              message={errorMessage}
              onDismiss={() => setErrorMessage('')}
              className="mb-6"
            />

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-secondary-700 mb-2">
                    {t.contact.nameRequired}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-secondary-400" />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder={t.contact.namePlaceholder}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-secondary-700 mb-2">
                    {t.contact.phone}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-secondary-400" />
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder={t.contact.phonePlaceholder}
                    />
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-secondary-700 mb-2">
                  {t.contact.subjectRequired}
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                  placeholder={t.contact.subjectPlaceholder}
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-secondary-700 mb-2">
                  {t.contact.messageRequired}
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-secondary-400" />
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-3 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 resize-none"
                    placeholder={t.contact.messagePlaceholder}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                loading={isLoading}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
                size="lg"
              >
                {isLoading ? t.contact.sending : t.contact.send}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ContactScreen 